import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OriginPage } from 'src/Libs/wallboxes/types'
import { useTypedSelector } from 'src/State/RootReducer'
import { getDynamicEmsSettings, updateDynamicEmsSettings } from '../api/dynamic-ems.api'
import { getReferencePriceSettings, updateReferencePriceSettings, updateDynamicPricesSettings } from '../api/dynamic-prices-settings.api'
import {
  DynamicEmsSettingsModel,
  DynamicPricesModel,
  DynamicPricesSettings,
  EnergyDirection,
  ReferencePriceModel,
  UpdateDynamicEmsSettingsModel,
} from '../types'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { getDynamicEmsSettingsQueryKey, useIsDynamicEmsAccessible } from './dynamic-ems'
import { ApiResult, usePortalMutation, usePortalQueries } from 'src/Libs/api'
import { BannerKey, useHideBanner } from 'src/Libs/banners'
import { useUserSettings } from 'src/Hooks'
import { isEqual } from 'lodash'
import { getDynamicPowerPriceQueryKey, useFetchDynamicPowerPrice } from './use-fetch-dynamic-power-price'
import { useQueryClient } from '@tanstack/react-query'
import { useFarmContext } from 'src/Elements/Farm'
import { preserveExpectedUnavailableResult } from '../utils/preserve-expected-unavailable-result'
import { triggerConfigSync } from 'src/Libs/installations/api/config-sync.api'
import { isFlowTheme } from 'src/Libs/utils'
import { resolveDynamicEmsFlags } from '../utils/price-limit-settings'

type ReferencePriceQueryData = ApiResult<ReferencePriceModel, boolean>

export const getBatteryReferencePriceQueryKey = (storageId: string | undefined) => ['battery-reference-price', storageId] as const

interface UseDynamicPrices {
  isLoading: boolean
  isError: boolean
  isUpdating: boolean
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
}

export const dynamicEmsSettingsWithAvailabilityQueryKey = (installationId: string | undefined) =>
  ['dynamic-ems-settings-with-availability', installationId] as const

/**
 * The useDynamicPrices hook manages the fetching and updating of dynamic pricing settings for a specific installation.
 * It returns the current settings and an updater function. The hook also handles error reporting through a snackbar notification system.
 */
const useDynamicPricesSettings = ({ storageId, origin }: { storageId: string | undefined; origin?: OriginPage }): UseDynamicPrices => {
  const installationId = useTypedSelector(state => state.storage.installation?.id)?.toString()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const { enqueueErrorStack } = useSnackbar()
  const [dynamicPricesSettingsState, setDynamicPricesSettingsState] = useState<DynamicPricesModel | null>(null)
  const [referencePriceState, setReferencePriceState] = useState<string | null>(null)
  const [dynamicEmsSettingsState, setDynamicEmsSettingsState] = useState<UpdateDynamicEmsSettingsModel | null>(null)
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true

  let pageName = t('systemSettings.smartFunctions.smartCharge.pageTitle')
  if (origin === OriginPage.wallbox) {
    pageName = t('framework.wallbox')
  } else if (origin === OriginPage.Tariffs) {
    pageName = t('framework.tariffs')
  }

  const { isDynamicEmsAccessible, isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { hideBannerPermanently } = useHideBanner()
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()
  const { userSettings, updateUserSettings } = useUserSettings()
  const accordionName = t('systemSettings.price-based-charging.section-title')
  const {
    isLoading: isDynamicPricesSettingsLoading,
    isError: isDynamicPricesSettingsError,
    dynamicPowerPrices: dynamicPriceSettingsData,
  } = useFetchDynamicPowerPrice(storageId, !isServerOffline)

  /*
   * at the moment, we need to fetch battery reference price value from another endpoint
   */
  const [referencePriceQuery, dynamicEmsQuery] = usePortalQueries<ReferencePriceQueryData | DynamicEmsSettingsModel>([
    {
      queryKey: getBatteryReferencePriceQueryKey(storageId),
      queryFn: () => preserveExpectedUnavailableResult(() => getReferencePriceSettings(storageId, authFetch)),
      enabled: Boolean(storageId),
    },
    {
      queryKey: getDynamicEmsSettingsQueryKey(installationId),
      queryFn: () => getDynamicEmsSettings(installationId, authFetch),
      enabled: isDynamicEmsAccessible && Boolean(storageId),
    },
  ])
  const isReferencePriceBatteryLoading = referencePriceQuery.isLoading
  const isReferencePriceBatteryError = referencePriceQuery.isError
  const referencePriceBatteryData = (referencePriceQuery.data as ReferencePriceQueryData | null | undefined)?.value
  const isDynamicEmsSettingsLoading = dynamicEmsQuery.isLoading
  const isDynamicEmsSettingsError = dynamicEmsQuery.isError
  const dynamicEmsSettingsData = dynamicEmsQuery.data as DynamicEmsSettingsModel | null | undefined

  const { mutateAsync: handleDynamicPricesSettingsUpdateMutation, isPending: isUpdating } = usePortalMutation({
    mutationFn: async (newValues: DynamicPricesSettings) => {
      const { referencePrice: newReferencePrice, isDynamicEmsEnabledForBattery, isSgReadyEnabled, ...newDynamicPriceSettings } = newValues
      const { isDynamicEmsEnabledForBattery: newIsDynamicEmsEnabledForBattery, isSgReadyEnabled: newIsSgReadyEnabled } = resolveDynamicEmsFlags(
        { isDynamicEmsEnabledForBattery, isSgReadyEnabled },
        dynamicEmsSettingsState
      )
      const isDynamicEmsDeactivationRequested = isDynamicEmsEnabledForBattery === false

      setDynamicPricesSettingsState(newDynamicPriceSettings)

      if (newReferencePrice) setReferencePriceState(newReferencePrice)

      const updatedIsSgReadyEnabled = newIsSgReadyEnabled && newIsDynamicEmsEnabledForBattery

      if (newIsDynamicEmsEnabledForBattery !== undefined) {
        setDynamicEmsSettingsState({
          isDynamicEmsEnabledForBattery: newIsDynamicEmsEnabledForBattery,
          isSgReadyEnabled: updatedIsSgReadyEnabled,
          feedInTariffType: isDynamicEmsDeactivationRequested ? null : dynamicEmsSettingsState?.feedInTariffType,
        })
      }

      await handleUpdateSettings(
        newDynamicPriceSettings,
        newReferencePrice,
        newIsDynamicEmsEnabledForBattery,
        updatedIsSgReadyEnabled,
        isDynamicEmsDeactivationRequested
      )
      return new ApiResult<void>()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getDynamicPowerPriceQueryKey(storageId) })
      queryClient.invalidateQueries({ queryKey: getBatteryReferencePriceQueryKey(storageId) })
      queryClient.invalidateQueries({ queryKey: getDynamicEmsSettingsQueryKey(installationId) })
      handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName, showWarningSnackbar: true, isEmsSetting: true })
    },
    onError: error => {
      console.error(error)
      enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName }))

      /* revert changes */
      setDynamicPricesSettingsState(dynamicPricesSettingsState)
      setReferencePriceState(referencePriceState)
      setDynamicEmsSettingsState(dynamicEmsSettingsState)
    },
  })

  // Need to use useLayoutEffect here to ensure that the state is updated before the component renders, preventing false blank view renders.
  const handleDynamicPricesSettingsUpdate = async (newValues: DynamicPricesSettings): Promise<void> => {
    await handleDynamicPricesSettingsUpdateMutation(newValues)
  }

  useLayoutEffect(() => {
    if (isDynamicPricesSettingsError) {
      setDynamicPricesSettingsState(null)
    } else if (dynamicPriceSettingsData) {
      setDynamicPricesSettingsState(dynamicPriceSettingsData)
    }
  }, [dynamicPriceSettingsData, isDynamicPricesSettingsError])

  useLayoutEffect(() => {
    setReferencePriceState(referencePriceBatteryData?.referencePrice ?? '0')
  }, [referencePriceBatteryData])

  useEffect(() => {
    if (dynamicEmsSettingsData) setDynamicEmsSettingsState(dynamicEmsSettingsData)
    if (dynamicEmsSettingsData?.isDynamicEmsEnabledForBattery) {
      if (!userSettings.dynamicEmsEnabledOnce) updateUserSettings({ dynamicEmsEnabledOnce: true })

      hideBannerPermanently(BannerKey.DynamicEmsBanner)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicEmsSettingsData])

  const syncConfigAfterDynamicPricesUpdate = async (): Promise<void> => {
    if (!isFlowTheme || !installationId) return

    try {
      const syncResult = await triggerConfigSync(authFetch, Number(installationId))
      if (syncResult.hasErrors) console.error('Config sync after dynamic power price update failed')
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * This function updates 1-3 settings based on the changes in the form.
   */
  const handleUpdateSettings = async (
    newDynamicPriceSettings: DynamicPricesModel,
    newReferencePrice: string | undefined,
    newIsDynamicEmsEnabledForBattery: boolean | undefined,
    newIsSgReadyEnabled: boolean | undefined,
    isDynamicEmsDeactivationRequested: boolean
  ): Promise<void> => {
    const hasDynamicPriceSettingsChanged = !isEqual(dynamicPricesSettingsState, newDynamicPriceSettings)
    const hasDynamicEmsSettingsChanged = dynamicEmsSettingsState?.isDynamicEmsEnabledForBattery !== newIsDynamicEmsEnabledForBattery
    const hasSgReadyChanged = dynamicEmsSettingsState?.isSgReadyEnabled !== newIsSgReadyEnabled
    const hasNewReferencePrice = referencePriceState !== newReferencePrice
    const updatePromises: Promise<ApiResult<unknown, boolean>>[] = []

    if (hasDynamicPriceSettingsChanged || hasDynamicEmsSettingsChanged) {
      updatePromises.push(
        updateDynamicPricesSettings(
          storageId,
          { ...newDynamicPriceSettings, isDynamicEmsEnabledForBattery: newIsDynamicEmsEnabledForBattery },
          authFetch
        ).then(async result => {
          await syncConfigAfterDynamicPricesUpdate()
          return result
        })
      )
    }

    // Don't send this request for AI360 2.0
    if (!isDynamicEmsV2Accessible && (hasDynamicEmsSettingsChanged || hasSgReadyChanged)) {
      updatePromises.push(
        updateDynamicEmsSettings(
          installationId,
          { isDynamicEmsEnabledForBattery: newIsDynamicEmsEnabledForBattery, isSgReadyEnabled: newIsSgReadyEnabled },
          authFetch
        )
      )
    }

    // Turn off AI360 2.0 if '360 Price Threshold' or 'No' option was selected
    if (isDynamicEmsV2Accessible && dynamicPricesSettings?.isDynamicEmsEnabledForBattery && isDynamicEmsDeactivationRequested) {
      updatePromises.push(
        updateDynamicEmsSettings(installationId, { isDynamicEmsEnabledForBattery: false, isSgReadyEnabled: false, feedInTariffType: null }, authFetch)
      )
    }

    if (hasNewReferencePrice && storageId) {
      updatePromises.push(updateReferencePriceSettings(storageId, newReferencePrice, EnergyDirection.Charge, authFetch))
    }

    await Promise.all(updatePromises)
  }

  const isLoading = isDynamicPricesSettingsLoading || isReferencePriceBatteryLoading || isDynamicEmsSettingsLoading
  const isError = isDynamicPricesSettingsError || isReferencePriceBatteryError || isDynamicEmsSettingsError

  const dynamicPricesSettings: DynamicPricesSettings | null = useMemo(() => {
    if (!isError && dynamicPricesSettingsState && referencePriceState) {
      return {
        ...dynamicPricesSettingsState,
        referencePrice: referencePriceState,
        // AI360 1.0 uses only battery value, AI360 2.0 uses two values (battery and feed-in), so we need to check both for the flag
        isDynamicEmsEnabledForBattery: isDynamicEmsV2Accessible
          ? dynamicEmsSettingsState?.isDynamicEmsEnabledForBattery || !!dynamicEmsSettingsState?.feedInTariffType
          : dynamicEmsSettingsState?.isDynamicEmsEnabledForBattery,
        isSgReadyEnabled: dynamicEmsSettingsState?.isSgReadyEnabled,
        feedInTariffType: dynamicEmsSettingsState?.feedInTariffType ?? null,
      }
    } else return null
  }, [
    isError,
    dynamicPricesSettingsState,
    referencePriceState,
    isDynamicEmsV2Accessible,
    dynamicEmsSettingsState?.isDynamicEmsEnabledForBattery,
    dynamicEmsSettingsState?.feedInTariffType,
    dynamicEmsSettingsState?.isSgReadyEnabled,
  ])

  return {
    isLoading,
    isError,
    dynamicPricesSettings,
    handleDynamicPricesSettingsUpdate,
    isUpdating,
  }
}

export default useDynamicPricesSettings
